import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import TermsAcceptanceModal from './TermsAcceptanceModal';
import type { AgreementType } from '../../legal';

const PENDING_KEY = 'pending_agreements';
const TOKEN_KEY = 'token';

const VALID_TYPES: AgreementType[] = ['web_tos', 'privacy_policy'];

function readPending(): AgreementType[] {
  try {
    const raw = localStorage.getItem(PENDING_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Web app only knows how to render web_tos + privacy_policy. driver_eula
    // would only appear for drivers (mobile users), but we filter defensively.
    return parsed.filter((t): t is AgreementType => VALID_TYPES.includes(t as AgreementType));
  } catch {
    return [];
  }
}

/**
 * Global click-wrap gate. Mounted once in App.tsx; reads the
 * `pending_agreements` localStorage key (populated by fetchAndStoreUser
 * when /auth/me reports a `requires_terms_acceptance` gap) and shows a
 * blocking modal until the user accepts or declines.
 *
 * Covers the Google OAuth flow (where the redirect bypasses Login.tsx's
 * inline modal) and version-bump scenarios for already-logged-in users.
 *
 * Idempotent with Login.tsx's inline modal: by the time the user reaches
 * the dashboard, Login.tsx has already cleared `pending_agreements` (the
 * inline accept POST → next /me returns empty), so this gate stays silent.
 */
export const TermsGate: React.FC = () => {
  const [pending, setPending] = useState<AgreementType[]>([]);
  const navigate = useNavigate();
  const location = useLocation();

  // Suppress on unauthenticated public routes — they have their own click-
  // wrap flows (Register), or are pre-auth (Login, ForgotPassword).
  const isPublicRoute = ['/login', '/register', '/forgot-password', '/'].includes(
    location.pathname
  ) || location.pathname.startsWith('/verify-email');

  useEffect(() => {
    const recheck = () => {
      const token = localStorage.getItem(TOKEN_KEY);
      if (!token) {
        setPending([]);
        return;
      }
      setPending(readPending());
    };

    recheck();
    window.addEventListener('user-updated', recheck);
    window.addEventListener('storage', recheck);
    return () => {
      window.removeEventListener('user-updated', recheck);
      window.removeEventListener('storage', recheck);
    };
  }, [location.pathname]);

  if (isPublicRoute || pending.length === 0) return null;

  const handleAccept = () => {
    // Modal already POSTs to /agreements/accept and writes localStorage via
    // storeAcceptance. Clear the gate flag so we don't re-prompt.
    localStorage.removeItem(PENDING_KEY);
    setPending([]);
  };

  const handleDecline = () => {
    // Decline = log the user out. They cannot use the app without accepting.
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    localStorage.removeItem('company_id');
    localStorage.removeItem('company_name');
    localStorage.removeItem(PENDING_KEY);
    setPending([]);
    navigate('/login', { replace: true });
  };

  return (
    <TermsAcceptanceModal
      open={true}
      agreementTypes={pending}
      onAccept={handleAccept}
      onDecline={handleDecline}
      blocking={true}
    />
  );
};

export default TermsGate;
